<template>
    <div class="mt-6 hide_scroll overflow-y-auto">
        <div>
            <FormKit
                type="text"
                label="Group name"
                placeholder="Enter group name"
                label-class="!text-black-900 !text-xs !font-normal"
                wrapper-class="!max-w-full"
                input-class="placeholder:text-gray-200 !font-medium !text-black-900"
                @input="() => (errors = [])"
                :errors="errors"
                v-model="groupName"
            />
        </div>

        <GroupMembersContainer :groupMembers="groupMembers" @handleCancel="handleCancel" />

        <div class="mt-6" v-if="allGroups.length">
            <p class="pb-2.5 border-b border-gray-150 text-black-900 font-semibold mb-6">Or use existing chat</p>
            <template v-for="(group, index) in allGroups" :key="index">
                <div
                    class="mb-2 flex gap-2 cursor-pointer hover:bg-gray-100 p-2 rounded-lg"
                    @click="() => addMemberToGroup(group._id)"
                >
                    <MutliContactNameIcon
                        :data="
                            group.users.map((user) => ({
                                first_name: user.firstName,
                                last_name: user.lastName,
                                badge: `name_icon_${user.firstName.charAt(0).toLowerCase()}`,
                            }))
                        "
                        :isFull="false"
                        :isShow="false"
                        :isLarge="true"
                    />
                    <p class="flex flex-col justify-center">
                        <span class="text-sm font-semibold text-black-900">{{ group.chatName }}</span>
                        <span class="text-xs font-normal text-black-900">{{
                            Array.isArray(group.latestMessage?.content)
                                ? "sent some file"
                                : group.latestMessage?.content
                        }}</span>
                    </p>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup>
import { groupMembers } from "./globalStates";
import GroupMembersContainer from "./GroupMembersContainer.vue";
import { useChatStore } from "@/stores/chatStore";
import MutliContactNameIcon from "@/components/contacts/MutliContactNameIcon.vue";
import AxiosChat from "@/config/AxiosChatConfig";
import { socketConn } from "@/stores/socketConn";
import useToggleModal from "@/composables/useToggleModal";
import { useEmitter } from "@/composables/useEmitter";
import { onMounted, onUnmounted, ref, watch } from "vue";
import { chatroomStore } from "@/stores/messagesStore";
import { Toast } from "@/composables/Toast";

const emits = defineEmits(["backToAddMembers"]);

// [info]: states
const groupName = ref("");
const errors = ref([]);

// [info]: composable
const { allGroups, loadingState, handleFetchAllChats, handleCreateGroupChat } = useChatStore();
const { toggleModal } = useToggleModal();
const emitter = useEmitter();
const { showToast } = Toast();

// [info]: methods
const addMemberToGroup = async (chatId) => {
    loadingState.startChat = true;
    try {
        const resp = await AxiosChat.put("chat/groupadd", {
            chatId,
            peerUsers: groupMembers.value.map((contact) => contact._id),
        });
        if (resp.status === 200) {
            const { users } = resp.data;
            chatroomStore.activeChatContact = resp.data;
            chatroomStore.chatRoom = resp.data;
            emitter.emit("getRoomMessages");
            socketConn.socket.emit("room created", users);
            await handleFetchAllChats(null, true);
            toggleModal("new_message");
        }
    } catch (e) {
        console.error("[ERR]: at adding member to group", e);
    } finally {
        loadingState.startChat = false;
    }
};

const handleCancel = (state, target) => {
    if (state === "cancel") groupMembers.value = groupMembers.value.filter((contact) => contact.id !== target.id);
};

const handleCreateGroup = async () => {
    if (!groupName.value) {
        errors.value = ["Name is required"];
        return;
    }
    loadingState.startChat = true;
    try {
        const res = await handleCreateGroupChat(groupName.value);
        if (res) {
            await handleFetchAllChats();
            showToast("Success", "Success", "Group Created Successfully!");
            toggleModal("new_message");
        }
    } catch (e) {
        console.error("[ERR]: err at handleCreateGroup", e);
    } finally {
        loadingState.startChat = false;
    }
};

// [info]: lifecycle
watch(
    () => groupMembers.value,
    () => {
        if (groupMembers.value.length < 2) emits("backToAddMembers");
    },
);

onMounted(() => {
    emitter.on("create_group", handleCreateGroup);
});

onUnmounted(() => {
    emitter.off("create_group", handleCreateGroup);
});
</script>
